import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useStoreState } from "ezStore";
import { intl } from "LocaleWrapper";
import { useApolloClient, useMutation } from "@apollo/client";

import { Table, Filter, FilterValues, PageHeader, Card, Button, showSnackbar, FilterTypes, emptyFunction } from "@cofano/cck";
import { HtmlEvent } from "@cofano/cck/Table/Table";
import { Box } from "@mui/material";
import InsertCommentIcon from "@mui/icons-material/InsertComment";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

import ContractStatusSelect, { ContractStatusSelectOption } from "components/contract/ContractStatusSelect";
import useLayout from "components/layout/useLayout";
import { entityWithDocument, openSidebarWithPdf } from "components/pdf/PdfContainer";
import { TableId, useBackendTable } from "components/table";
import ContractStatusChanger from "components/contract/ContractStatusChanger";
import { useConfirm } from "components/hooks/useConfirm";
import Helmet from "components/helmet/Helmet";
import CommentsWidget from "components/comments/CommentsWidget";
import useComments from "components/comments/useComments";
import CommentIndicatorButton from "components/comments/CommentIndicatorButton";

import LabelSelect, { LabelSelectOption } from "view/labels/components/selects/LabelSelect";
import RelationSelect, { RelationSelectOption } from "view/relations/components/selects/RelationSelect";
import CharterSubtypeSelect from "view/charters/components/selects/CharterSubtypeSelect";
import CharterSubtypeTag from "view/charters/components/tags/CharterSubtypeTag";
import CharterTypeSelect from "view/charters/components/selects/CharterTypeSelect";
import CharterTypeTag from "view/charters/components/tags/CharterTypeTag";

import DuplicateMultipleContractsDialog from "view/contractsNew/components/dialogs/DuplicateMultipleContractsDialog";
import ContractExtendDialog from "view/contractsNew/components/dialogs/ContractExtendDialog";
import LabelTag from "view/labels/components/tags/LabelTag";
import {
	DataFormat,
	Fields,
	hasOptionalFieldPermission,
	hasPluginPermission,
	Language,
	usePermission,
} from "imports/Utils";

import { RelationLink } from "utils/LinkUtils";
import { FormattedDate, FormattedDateWindow } from "utils/DateUtils";

import { CharterSubtype, CharterType } from "__generated__/enums";

import { ChildPropsFromPagination } from "types/commons";
import { CommentEntityType, LabelType, RelationType, SortDirection } from "__generated__/enums";
import { GetContractsDocument, GetContractsQuery } from "__generated__/view/contractsNew/graphql/contract.queries.generated";
import { DeleteContractDocument } from "__generated__/view/contractsNew/graphql/contract.mutations.generated";

type ContractFromQuery = ChildPropsFromPagination<GetContractsQuery, "contracts">;

const Contracts = () => {

	const { isReadOnly, isRTR, isFinanceAdmin } = usePermission();
	const session = useStoreState(store => store.session);

	const client = useApolloClient();

	const confirm = useConfirm();
	const layout = useLayout();
	const navigate = useNavigate();

	const [ bulkDuplicate, setBulkDuplicate ] = useState<ContractFromQuery[]>([]);
	const [ extendContracts, setExtendContracts ] = useState<ContractFromQuery[]>([]);

	const [ deleteContract ] = useMutation(DeleteContractDocument);

	const { tableProps, filterProps, filterValuesProps, refetch, refetchAfterDelete } = useBackendTable({
		id: TableId.Contracts,
		selectSettings: {
			selectionEnabled: true,
			isFullSelectEnabled: false,
		},
		onChange: async (pagination, sort, filter, callback) => {
			try {
				const result = await client.query({
					query: GetContractsDocument,
					variables: {
						pagination: {
							...pagination,
							sortList: sort ?? [ {
								field: "createdOn",
								direction: SortDirection.Desc,
							} ],
						},
						search: {
							charterType: filter.charterType,
							charterSubtypes: filter.charterSubtype,
							contractNumber: filter.contractNumber,
							version: filter.versionNumber,
							description: filter.description,
							labels: filter.labels?.map((label: LabelSelectOption) => label.id) || [],
							creditors: filter.creditor ? filter.creditor.map((relation: RelationSelectOption) => relation.id) : [],
							debtors: filter.debtor ? filter.debtor.map((relation: RelationSelectOption) => relation.id) : [],
							status: filter.status?.map((status: ContractStatusSelectOption) => status.name) ?? [],
							validFrom: filter.validFrom ? filter.validFrom[0] : undefined,
							validUntil: filter.validFrom ? filter.validFrom[1] : undefined,
							expired: filter.expired,
							isParent: filter.isParentContract,
						},
					},
				});

				callback({
					content: result.data.contracts.content ?? [],
					totalResults: result.data.contracts.totalElements || 0,
				});
			} catch {
				callback({
					content: [],
					totalResults: 0,
				});
			}
		},
	});

	const { commentsProps, openComment } = useComments({
		contracts: (tableProps.content as ContractFromQuery[]).map(it => ({ ...it, contractNumber: it.contractNumber ?? "" })),
		onChange: () => refetch(),
	});

	const onDelete = (id: number) => {
		deleteContract({
			variables: {
				id: id,
			},
		}).then(() => {
			refetchAfterDelete();
			showSnackbar(intl.formatMessage({ id: "SUCCESSFUL_DELETED" }), { variant: "success" });
		});
	};

	return (
		<div>
			<PageHeader>
				<PageHeader.Title>
					{Language("CONTRACTS")}
					<Helmet title={intl.formatMessage({ id: "CONTRACTS" })} />
				</PageHeader.Title>

				<PageHeader.Actions>
					{!isReadOnly && isRTR && (
						<Button
							onClick={(event) => {
								if (event.ctrlKey) {
									window.open("/view/contracts/create", "_blank");
								} else {
									navigate("/view/contracts/create" );
								}
							}}>
							{Language("ADD_BUTTON")}
						</Button>
					)}
				</PageHeader.Actions>
			</PageHeader>

			{filterValuesProps.values.length > 0 && (
				<Box mb={2}>
					<Card disablePadding>
						<FilterValues
							{...filterValuesProps}
							fields={[ {
								id: "contractNumber",
								title: intl.formatMessage({ id: "CONTRACT_NUMBER" }),
							}, {
								id: "charterType",
								title: intl.formatMessage({ id: "TYPE" }),
								renderFilterValue: (value) => intl.formatMessage({ id: value }),
							}, {
								id: "charterSubtype",
								title: intl.formatMessage({ id: "CHARTER_TYPE" }),
								renderFilterValue: (value) => value.map((option: string) => intl.formatMessage({ id: option })).join(", "),
							}, {
								id: "status",
								title: intl.formatMessage({ id: "STATUS" }),
								renderFilterValue: (value) => value?.map((status: ContractStatusSelectOption) => intl.formatMessage({ id: `${status.languageKeyPrefix}${status.name}` })).join(", "),
							}, {
								id: "labels",
								title: intl.formatMessage({ id: "LABELS" }),
								renderFilterValue: (value) => value.map((option: LabelSelectOption) => option.text).join(", "),
							}, {
								id: "creditor",
								title: intl.formatMessage({ id: "INVOICE_CREDITOR" }),
								renderFilterValue: (value) => value.map((option: RelationSelectOption) => option.name).join(", "),
							}, {
								id: "debtor",
								title: intl.formatMessage({ id: "INVOICE_DEBTOR" }),
								renderFilterValue: (value) => value.map((option: RelationSelectOption) => option.name).join(", "),
							}, {
								id: "validFrom",
								title: intl.formatMessage({ id: "VALID_BETWEEN" }),
								renderFilterValue: (value) => <FormattedDateWindow from={value[0]} to={value[1]} format="ll" />,
							}, {
								id: "versionNumber",
								title: `${intl.formatMessage({ id: "TEMPLATES_VERSION" })} #`,
							}, {
								id: "description",
								title: intl.formatMessage({ id: "DESCRIPTION" }),
							}, {
								id: "expired",
								title: intl.formatMessage({ id: "EXPIRED" }),
								renderFilterValue: (value) => DataFormat.booleanAsText(value),
							}, {
								id: "isParentContract",
								title: intl.formatMessage({ id: "PARENT_CONTRACT" }),
								renderFilterValue: (value) => DataFormat.booleanAsText(value),
							} ]} />
					</Card>
				</Box>
			)}

			<Card disablePadding>
				<Table
					{...tableProps}
					onRowDoubleClick={(row, event) => {
						const url = `/view/contracts/${row.id}/details`;

						if (event.ctrlKey) {
							window.open(url);
						} else {
							navigate(url);
						}
					}}
					filterComponent={() => (
						<Filter
							{...filterProps}
							fields={[ {
								id: "contractNumber",
								title: intl.formatMessage({ id: "CONTRACT_NUMBER" }),
								filterType: FilterTypes.TEXT,
							}, {
								id: "charterSubtype",
								title: intl.formatMessage({ id: "CHARTER_TYPE" }),
								disabled: !hasPluginPermission(session, "useCharterSubtypes"),
								render: (value, onChange) => (
									<CharterSubtypeSelect
										value={value}
										onChange={options => onChange(options)}
										customCharterSubtypes={Object.values(CharterSubtype)}
										multiple
									/>
								),
							}, {
								id: "charterType",
								title: intl.formatMessage({ id: "TYPE" }),
								render: (value, onChange) => (
									<CharterTypeSelect
										value={value}
										onChange={onChange}
										disableSearch
									/>
								),
							}, {
								id: "status",
								title: intl.formatMessage({ id: "STATUS" }),
								render: (value, onChange) => (
									<ContractStatusSelect
										value={value}
										onChange={onChange}
										multiple
									/>
								),
							}, {
								id: "labels",
								title: intl.formatMessage({ id: "LABELS" }),
								render: (value, onChange) => (
									<LabelSelect
										value={value}
										onChange={onChange}
										type={LabelType.Contract}
										multiple />
								),
								disabled: !hasOptionalFieldPermission(session, Fields.CONTRACT, "labels"),
							}, {
								id: "creditor",
								title: intl.formatMessage({ id: "INVOICE_CREDITOR" }),
								render: (value, onChange) => (
									<RelationSelect
										value={value}
										onChange={options => onChange(options)}
										types={[
											RelationType.Supplier,
											RelationType.Operator,
											RelationType.Customer,
											RelationType.ShipManager,
											RelationType.Skipper ]}
										multiple />
								),
							}, {
								id: "debtor",
								title: intl.formatMessage({ id: "INVOICE_DEBTOR" }),
								render: (value, onChange) => (
									<RelationSelect
										value={value}
										onChange={options => onChange(options)}
										types={[
											RelationType.Supplier,
											RelationType.Operator,
											RelationType.Customer,
											RelationType.ShipManager,
											RelationType.Skipper ]}
										multiple />
								),
							}, {
								id: "validFrom",
								title: intl.formatMessage({ id: "VALID_BETWEEN" }),
								filterType: FilterTypes.DATE_RANGE,
							}, {
								id: "versionNumber",
								title: `${intl.formatMessage({ id: "TEMPLATES_VERSION" })} #`,
								filterType: FilterTypes.TEXT,
								disabled: !hasOptionalFieldPermission(session, Fields.CONTRACT, "versionNumber"),
							}, {
								id: "description",
								title: intl.formatMessage({ id: "DESCRIPTION" }),
								filterType: FilterTypes.TEXT,
							}, {
								id: "expired",
								title: intl.formatMessage({ id: "EXPIRED" }),
								filterType: FilterTypes.BOOLEAN,
							}, {
								id: "isParentContract",
								title: intl.formatMessage({ id: "PARENT_CONTRACT" }),
								filterType: FilterTypes.BOOLEAN,
							} ]} />
					)}
					columns={[ {
						id: "contractNumber",
						title: intl.formatMessage({ id: "CONTRACT_NUMBER" }),
						sortable: true,
						filterComponent: FilterTypes.TEXT,
						width: 150,
					}, {
						id: "charterSubtype",
						title: intl.formatMessage({ id: "CHARTER_TYPE" }),
						render: (row) => <CharterSubtypeTag type={row.charterSubtype} />,
						width: 150,
						disabled: !hasPluginPermission(session, "useCharterSubtypes"),
						filterComponent: (value, onChange) => (
							<CharterSubtypeSelect
								label={intl.formatMessage({ id: "CHARTER_TYPE" })}
								value={value}
								onChange={options => onChange(options)}
								customCharterSubtypes={Object.values(CharterSubtype)}
								multiple
							/>
						),
					}, {
						id: "charterType",
						title: intl.formatMessage({ id: "TYPE" }),
						render: (row) => <CharterTypeTag type={row.charterType} />,
						sortable: true,
						width: 150,
						filterComponent: (value, onChange) => (
							<CharterTypeSelect
								label={intl.formatMessage({ id: "TYPE" })}
								value={value}
								onChange={onChange}
								disableSearch
							/>
						),
					}, {
						id: "status",
						title: intl.formatMessage({ id: "STATUS" }),
						render: (row: ContractFromQuery) => (
							<ContractStatusChanger
								contract={row}
								refetch={refetch}
								disabled={isReadOnly || !(isRTR || isFinanceAdmin)} />
						),
						sortable: true,
						width: 200,
						filterComponent: (value, onChange) => (
							<ContractStatusSelect label={intl.formatMessage({ id: "STATUS" })}
								value={value}
								multiple
								onChange={onChange}
							/>
						),
					}, {
						id: "labels",
						title: intl.formatMessage({ id: "LABELS" }),
						render: (row: ContractFromQuery) => {
							return row.labels?.map((label, index) => (
								<span key={index} style={{ display: "inline-flex", margin: "1px" }}>
									<LabelTag label={label} />
								</span>
							));
						},
						disabled: !hasOptionalFieldPermission(session, Fields.CONTRACT, "labels"),
						width: 200,
						filterComponent: (value, onChange) => (
							<LabelSelect
								label={intl.formatMessage({ id: "LABELS" })}
								value={value}
								onChange={onChange}
								type={LabelType.Contract}
								multiple />
						),
					}, {
						id: "statusUpdatedOn",
						title: intl.formatMessage({ id: "CONTRACT_STATUS_UPDATED_ON" }),
						render: (row: ContractFromQuery) => DataFormat.timeSinceDate(row.statusUpdatedOn) || Language("UNKNOWN"),
						sortable: true,
						disabled: !hasOptionalFieldPermission(session, Fields.CONTRACT, "statusUpdatedOn"),
						width: 200,
					}, {
						id: "creditor",
						title: intl.formatMessage({ id: "INVOICE_CREDITOR" }),
						render: (row: ContractFromQuery) => <RelationLink relation={row.creditor} />,
						sortable: true,
						sortKey: "creditor.name",
						width: 200,
						filterComponent: (value, onChange) => (
							<RelationSelect
								label={intl.formatMessage({ id: "INVOICE_CREDITOR" })}
								value={value}
								onChange={options => onChange(options)}
								types={[
									RelationType.Supplier,
									RelationType.Operator,
									RelationType.Customer,
									RelationType.ShipManager,
									RelationType.Skipper ]}
								multiple />
						),
					}, {
						id: "debtor",
						title: intl.formatMessage({ id: "INVOICE_DEBTOR" }),
						render: (row: ContractFromQuery) => <RelationLink relation={row.debtor} />,
						sortable: true,
						sortKey: "debtor.name",
						width: 200,
						filterComponent: (value, onChange) => (
							<RelationSelect
								label={intl.formatMessage({ id: "INVOICE_DEBTOR" })}
								value={value}
								onChange={options => onChange(options)}
								types={[
									RelationType.Supplier,
									RelationType.Operator,
									RelationType.Customer,
									RelationType.ShipManager,
									RelationType.Skipper ]}
								multiple />
						),
					}, {
						id: "validFrom",
						title: intl.formatMessage({ id: "VALID_BETWEEN" }),
						render: (row: ContractFromQuery) => row.validFrom ? <FormattedDateWindow from={row.validFrom} to={row.validUntil} format="ll" /> : "",
						sortable: true,
						width: 250,
						filterComponent: FilterTypes.DATE_RANGE,
					}, {
						id: "versionNumber",
						title: `${intl.formatMessage({ id: "TEMPLATES_VERSION" })} #`,
						filterComponent: FilterTypes.TEXT,
						disabled: !hasOptionalFieldPermission(session, Fields.CONTRACT, "versionNumber"),
						sortable: true,
					}, {
						id: "description",
						title: intl.formatMessage({ id: "DESCRIPTION" }),
						sortable: true,
						filterComponent: FilterTypes.TEXT,
						width: 200,
					}, {
						id: "memo",
						title: intl.formatMessage({ id: "CONTRACT_MEMO" }),
						render: (row: ContractFromQuery) => DataFormat.stringShortened(row.memo, 20),
						disabled: !hasOptionalFieldPermission(session, Fields.CONTRACT, "memo"),
						width: 150,
					}, {
						id: "createdOn",
						title: intl.formatMessage({ id: "CREATED" }),
						render: (row: ContractFromQuery) => <FormattedDate date={row.createdOn} format="ll" />,
						sortable: true,
						width: 150,
					}, {
						id: "updatedOn",
						title: intl.formatMessage({ id: "LAST_UPDATED" }),
						render: (row: ContractFromQuery) => <FormattedDate date={row.updatedOn} format="ll" />,
						sortable: true,
						width: 150,
					}, {
						id: "expired",
						title: intl.formatMessage({ id: "EXPIRED" }),
						render: (row: ContractFromQuery) => DataFormat.boolean(row.expired),
						sortable: true,
						width: 150,
					}, {
						id: "isParentContract",
						title: intl.formatMessage({ id: "PARENT_CONTRACT" }),
						render: (row: ContractFromQuery) => DataFormat.boolean(!!row.isParentContract),
						sortable: true,
						width: 150,
					} ]}
					rowActions={isReadOnly ? undefined : [ {
						icon: <InsertCommentIcon />,
						onClick: emptyFunction,
						render: (row: ContractFromQuery) => (
							<CommentIndicatorButton
								entity={row}
								onClick={() => openComment(row.id, CommentEntityType.Contract)}
								session={session} />
						),
					}, {
						icon: <InsertDriveFileIcon />,
						onClick: (row: ContractFromQuery) => openSidebarWithPdf(entityWithDocument.CONTRACT, row.id, layout),
					}, {
						icon: <EditIcon />,
						onClick: (row: ContractFromQuery, event: HtmlEvent) => {
							if (event.ctrlKey) {
								window.open(`/view/contracts/${row.id}/edit`, "_blank");
							} else {
								navigate(`/view/contracts/${row.id}/edit`);
							}
						},
						hidden: isReadOnly || !isRTR,
						disabled: (row: ContractFromQuery) => !row.isEditable,
					}, {
						icon: <DeleteIcon />,
						hidden: isReadOnly || !isRTR,
						disabled: (row: ContractFromQuery) => !row.isEditable,
						onClick: (row: ContractFromQuery) => {
							confirm({
								type: "WARNING",
								title: row.isParentContract ? intl.formatMessage({ id: "CONFIRM_DELETE_PARENT_CONTRACT" }) : row.charters && row.charters.length > 0 ? intl.formatMessage({ id: "CONFIRM_DELETE_CONTRACT_USED_BY_CHARTER" }) : intl.formatMessage({ id: "CONFIRM_DELETE_DATA" }),
							}).then(() => {
								onDelete(row.id);
							});
						},
					} ]}
					actions={isReadOnly || !isRTR ? undefined : [ {
						title: intl.formatMessage({ id: "EXTEND_CONTRACTS" }),
						onClick: (selection: ContractFromQuery[] | null) => setExtendContracts(selection ?? []),
					}, {
						icon: <ContentCopyIcon fontSize="small" />,
						hidden: isReadOnly || !isRTR,
						disabled: (selection: ContractFromQuery[]) => selection.some((contract) => contract.charterType === CharterType.Sales),
						onClick: (selection: ContractFromQuery[] | null) => setBulkDuplicate(selection ?? []),
					} ].filter(x => !x.hidden)}
				/>
			</Card>

			{bulkDuplicate.length > 0 && (
				<DuplicateMultipleContractsDialog
					open={bulkDuplicate.length > 0}
					onClose={() => setBulkDuplicate([])}
					contracts={bulkDuplicate}
					refetch={refetch} />
			)}

			<ContractExtendDialog
				open={extendContracts.length > 0}
				onClose={() => setExtendContracts([])}
				contracts={extendContracts}
				refetch={refetch} />

			<CommentsWidget {...commentsProps} />
		</div>
	);
};

export default Contracts;
